import React, { useState, useEffect, useRef } from "react";
import { BiSolidSend } from "react-icons/bi";
import {
  FaArrowLeft,
  FaSearch,
  FaPaperclip,
  FaMicrophone,
} from "react-icons/fa";
import {
  MdOutlinePhoneCallback,
  MdVideocam,
  MdCheck,
  MdDoneAll,
} from "react-icons/md";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import { BsEmojiSmile } from "react-icons/bs";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { ChatListFn, CustomerListFn } from "../../Services/ChatAgent";
import { url } from "./RingTone";
import EmojiPicker from "./Emoji";
import { CircularProgress } from "@mui/material";

const ChatAgent = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [ws, setWs] = useState(null);
  const [searchValue, setSearchValue] = useState();
  const [file, setFile] = useState(null);
  const [chat, setChat] = useState();
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const { state } = useLocation();

  // pagination states
  const [page, setPage] = useState(1);
  const [chatList, setChatList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const previousScrollHeight = useRef(0);

  useEffect(() => {
    notificationSoundRef.current = new Audio(url);
  }, []);

  const playNotificationSound = () => {
    if (notificationSoundRef.current) {
      notificationSoundRef.current.currentTime = 0;
      notificationSoundRef.current
        .play()
        .catch((e) => console.log("Audio play failed:", e));
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender !== "me") {
        playNotificationSound();
      }
    }
  }, [messages]);

  const { data: customers } = useQuery(["c", searchValue], () =>
    CustomerListFn({
      search_value: searchValue,
      store_id: localStorage.getItem("store_id"),
    }),
  );

  const senders = customers?.data?.sender_username;
  const storeId = customers?.data?.store_id;

  // chat pagination query
  const { data: ChatData, isFetching } = useQuery(
    ["chat", chat?.user_name, page],
    () =>
      ChatListFn({
        reciever_username: chat?.user_name,
        page: page,
        store_id: localStorage.getItem("store_id"),
      }),
    {
      enabled: !!chat,
      keepPreviousData: true,
      onSuccess: (data) => {
        const newData = data?.data?.data || [];
        if (newData.length === 0) {
          setHasMore(false);
        } else {
          if (page === 1) {
            // Initial load - scroll to bottom
            setChatList(newData);
            setIsInitialLoad(true);
          } else {
            // Loading older messages - maintain scroll position
            setChatList((prev) => [...newData.reverse(), ...prev]);
          }
        }
      },
    },
  );

  // Scroll to bottom on initial load
  const scrollToBottom = (behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Handle initial load scroll to bottom
  useEffect(() => {
    if (isInitialLoad && chatList.length > 0) {
      setTimeout(() => {
        scrollToBottom("auto"); // Instant scroll for initial load
        setIsInitialLoad(false);
      }, 100);
    }
  }, [isInitialLoad, chatList]);

  // Auto-scroll to bottom when new real-time messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom("smooth");
    }
  }, [messages]);

  // Maintain scroll position when loading older messages
  useEffect(() => {
    if (page > 1 && messageContainerRef.current && !isInitialLoad) {
      const container = messageContainerRef.current;
      const newScrollHeight = container.scrollHeight;
      const scrollDifference = newScrollHeight - previousScrollHeight.current;
      container.scrollTop = scrollDifference;
    }
  }, [chatList, page]);

  useEffect(() => {
    if (!chat || !senders) return;

    // const socket = new WebSocket(
    //   `ws://192.168.1.73:8989/ws/chatwithfile/${senders}/${chat?.user_name}/`
    // );

    const socket = new WebSocket(
      `wss://comblog.aarasocial.com/ws/chatwithfile/${senders}/${chat?.user_name}/`,
    );

    socket.onopen = () => console.log("WebSocket connected");
    socket.onmessage = (event) => {
      console.log(event.data);
      const data = JSON.parse(event?.data);
      if (data) {
        setMessages((prev) => [
          ...prev,
          {
            sender: data.sender,

            message: data.message,
            file: data.file,
            message_type: data.message_type,
            timestamp: new Date(),
            status: data.sender === senders ? "sent" : "received",
          },
        ]);
      }
    };
    socket.onerror = (err) => console.log("WS error:", err);
    socket.onclose = () => console.log("WS closed");

    setWs(socket);
    return () => socket.close();
  }, [chat, senders]);

  const handleScroll = (e) => {
    const container = e.target;
    const top = container.scrollTop;

    // Store current scroll height before loading more
    if (top === 0 && hasMore && !isFetchingMore && !isFetching) {
      previousScrollHeight.current = container.scrollHeight;
      setIsFetchingMore(true);
      setPage((prev) => prev + 1);
      setTimeout(() => setIsFetchingMore(false), 1000);
    }
  };

  const handleSendMessage = () => {
    if (ws && message.trim()) {
      const messageData = {
        message: message,
        receiver: chat?.user_name,
        sender: senders,
        store: storeId,
      };
      ws.send(JSON.stringify(messageData));
      setMessage("");
      setShowEmojiPicker(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const sendFile = async (file) => {
    if (!file) return alert("No file selected");
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const data = {
        sender: senders,
        receiver: chat?.user_name,
        store: storeId,
        message_type: "file",
        file: reader.result,
      };
      ws.send(JSON.stringify(data));
      setFile(null);
    };
    reader.onerror = (err) => console.error("File read error:", err);
  };

  const formatTime = (date) =>
    date
      ? new Date(date).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : "";

  const handleEmojiSelect = (emoji) => {
    setMessage(message + emoji);
    setShowEmojiPicker(false);
  };

  const {
    data: customersData,
    isLoading,
    error,
  } = useQuery(
    ["customerdata", state?.user_name],
    () =>
      CustomerListFn({
        username: state?.user_name,
        store_id: localStorage.getItem("store_id"),
      }),
    {
      enabled: !!state?.user_name,
    },
  );

  useEffect(() => {
    if (customersData?.data?.data) {
      setChat(customersData?.data?.data?.[0] ?? {});
    }
  }, [customersData]);

  return (
    // <>
    //   <div className="flex h-[88vh] bg-[#111B21]">
    //     {/* Sidebar */}
    //     <div className="bg-[#111B21] text-white w-[30%] min-w-[340px] flex flex-col border-r border-[#2A3942]">
    //       <div className="bg-[#202C33] px-4 py-3 flex justify-between items-center">
    //         <button
    //           onClick={() => navigate(-1)}
    //           className="p-2 hover:bg-[#2A3942] rounded-full transition-colors"
    //         >
    //           <FaArrowLeft className="text-[#8696A0]" />
    //         </button>
    //         <h2 className="text-lg font-medium">Chats</h2>
    //         <button className="p-2 hover:bg-[#2A3942] rounded-full transition-colors">
    //           <PiDotsThreeOutlineVerticalFill className="text-[#8696A0]" />
    //         </button>
    //       </div>

    //       <div className="px-3 py-2 bg-[#111B21]">
    //         <div className="bg-[#202C33] rounded-lg flex items-center px-4 py-2">
    //           <FaSearch className="text-[#8696A0] text-sm" />
    //           <input
    //             type="text"
    //             placeholder="Search or start new chat"
    //             className="bg-transparent text-white ml-4 flex-1 outline-none text-sm"
    //             onChange={(e) => setSearchValue(e.target.value)}
    //           />
    //         </div>
    //       </div>

    //       <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-[#374045] scrollbar-track-transparent">
    //         {customers?.data?.data?.map((customer, i) => (
    //           <div
    //             key={i}
    //             className={`flex items-center px-4 py-3 cursor-pointer hover:bg-[#202C33] transition-colors ${
    //               customer?.sender_id === chat?.sender_id ? "bg-[#2A3942]" : ""
    //             }`}
    //             onClick={() => {
    //               setChat(customer);
    //               setMessages([]);
    //               setChatList([]);
    //               setPage(1);
    //               setHasMore(true);
    //               setIsInitialLoad(false);
    //               previousScrollHeight.current = 0;
    //             }}
    //           >
    //             <div className="w-12 h-12 rounded-full bg-[#6B7C85] flex items-center justify-center overflow-hidden flex-shrink-0">
    //               {customer?.profile_picture ? (
    //                 <img
    //                   src={customer.profile_picture}
    //                   alt={customer?.full_name}
    //                   className="w-full h-full object-cover"
    //                 />
    //               ) : (
    //                 <span className="text-lg font-medium">
    //                   {customer?.full_name?.charAt(0) || "U"}
    //                 </span>
    //               )}
    //             </div>
    //             <div className="ml-4 border-b border-[#2A3942] pb-3 flex-1 min-w-0">
    //               <div className="flex justify-between items-baseline">
    //                 <h3 className="font-medium truncate">
    //                   {customer?.user_name}
    //                 </h3>
    //                 <span className="text-xs text-[#8696A0] flex-shrink-0 ml-2">
    //                   {formatTime(customer?.last_message_time)}
    //                 </span>
    //               </div>
    //             </div>
    //           </div>
    //         ))}
    //       </div>
    //     </div>

    //     {/* Chat Area */}
    //     <div className="flex-1 flex flex-col bg-[#0B141A] relative">
    //       {chat ? (
    //         <>
    //           {/* Chat Header */}
    //           <div className="bg-[#202C33] px-4 py-3 flex items-center justify-between border-b border-[#2A3942]">
    //             <div className="flex items-center gap-3">
    //               <div className="w-10 h-10 rounded-full bg-[#6B7C85] flex items-center justify-center overflow-hidden">
    //                 {chat?.profile_picture ? (
    //                   <img
    //                     src={chat.profile_picture}
    //                     alt={chat?.full_name}
    //                     className="w-full h-full object-cover"
    //                   />
    //                 ) : (
    //                   <span className="text-sm font-medium">
    //                     {chat?.full_name?.charAt(0) || "U"}
    //                   </span>
    //                 )}
    //               </div>
    //               <div>
    //                 <h3 className="text-white font-medium">
    //                   {chat?.user_name}
    //                 </h3>
    //                 <p className="text-xs text-[#8696A0]">
    //                   {isTyping ? "typing..." : "online"}
    //                 </p>
    //               </div>
    //             </div>
    //             {/* <div className="flex gap-4">
    //               <button className="text-[#8696A0] hover:text-white transition-colors">
    //                 <MdVideocam className="text-2xl" />
    //               </button>
    //               <button className="text-[#8696A0] hover:text-white transition-colors">
    //                 <MdOutlinePhoneCallback className="text-xl" />
    //               </button>
    //               <button className="text-[#8696A0] hover:text-white transition-colors">
    //                 <PiDotsThreeOutlineVerticalFill className="text-xl" />
    //               </button>
    //             </div> */}
    //           </div>

    //           {/* Message List */}
    //           <div
    //             ref={messageContainerRef}
    //             onScroll={handleScroll}
    //             className="flex-1 overflow-y-auto px-16 py-4 scrollbar-thin scrollbar-thumb-[#374045] scrollbar-track-transparent"
    //             style={{
    //               backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpattern id='a' patternUnits='userSpaceOnUse' width='100' height='100'%3E%3Cpath d='M0 0h100v100H0z' fill='%230b141a'/%3E%3C/pattern%3E%3Crect width='100' height='100' fill='url(%23a)'/%3E%3C/svg%3E")`,
    //             }}
    //           >
    //             {isFetchingMore && (
    //               <div className="text-center text-[#8696A0] text-sm py-2">
    //                 Loading older messages...
    //               </div>
    //             )}

    //             {/* Paginated chat messages */}
    //             {chatList.map((msg, i) => (
    //               <div
    //                 key={`chat-${i}`}
    //                 className={`flex mb-2 ${
    //                   msg.sender_username === senders
    //                     ? "justify-end"
    //                     : "justify-start"
    //                 }`}
    //               >
    //                 <div
    //                   className={`relative max-w-[65%] rounded-lg px-3 py-2 shadow-md ${
    //                     msg.sender_username === senders
    //                       ? "bg-[#005C4B]"
    //                       : "bg-[#202C33]"
    //                   } text-white`}
    //                 >
    //                   {msg.message_type === "text" && (
    //                     <p className="text-sm break-words">{msg.message}</p>
    //                   )}
    //                   {msg.message_type === "file" && (
    //                     <img
    //                       src={msg.file}
    //                       alt="file"
    //                       className="rounded-lg max-w-full"
    //                     />
    //                   )}
    //                   <div className="flex justify-end items-center gap-1 mt-1 text-[11px] text-[#8696A0]">
    //                     <span>
    //                       {formatTime(msg.timestamp || msg.created_at)}
    //                     </span>
    //                     {msg.sender_username === senders && (
    //                       <MdDoneAll className="text-sm" />
    //                     )}
    //                   </div>
    //                 </div>
    //               </div>
    //             ))}

    //             {/* Real-time websocket messages */}
    //             {messages.map((msg, i) => (
    //               <div
    //                 key={`live-${i}`}
    //                 className={`flex mb-2 ${
    //                   msg.sender === senders ? "justify-end" : "justify-start"
    //                 }`}
    //               >
    //                 <div
    //                   className={`relative max-w-[65%] rounded-lg px-3 py-2 shadow-md ${
    //                     msg.sender === senders ? "bg-[#005C4B]" : "bg-[#202C33]"
    //                   } text-white`}
    //                 >
    //                   {msg.message_type === "text" && (
    //                     <p className="text-sm break-words">{msg.message}</p>
    //                   )}
    //                   {msg.message_type === "file" && (
    //                     <img
    //                       src={`https://cbadmin.aarasocial.com/media/${msg.message[0]}`}
    //                       alt="file"
    //                       className="rounded-lg max-w-full"
    //                     />
    //                   )}
    //                   <div className="flex justify-end items-center gap-1 mt-1 text-[11px] text-[#8696A0]">
    //                     <span>{formatTime(msg.timestamp)}</span>
    //                     {msg.sender === senders && (
    //                       <MdCheck className="text-sm" />
    //                     )}
    //                   </div>
    //                 </div>
    //               </div>
    //             ))}

    //             <div ref={messagesEndRef} />
    //           </div>

    //           {/* Input */}
    //           <div className="bg-[#202C33] px-4 py-3 flex items-end gap-2 relative">
    //             {showEmojiPicker && (
    //               <div className="absolute bottom-full left-4 mb-2">
    //                 <EmojiPicker
    //                   onEmojiSelect={handleEmojiSelect}
    //                   onClose={() => setShowEmojiPicker(false)}
    //                 />
    //               </div>
    //             )}
    //             <button
    //               className="text-[#8696A0] hover:text-white transition-colors p-2"
    //               onClick={() => setShowEmojiPicker(!showEmojiPicker)}
    //             >
    //               <BsEmojiSmile className="text-2xl" />
    //             </button>

    //             <button
    //               className="text-[#8696A0] hover:text-white transition-colors p-2"
    //               onClick={() => fileInputRef.current?.click()}
    //             >
    //               <FaPaperclip className="text-xl" />
    //             </button>
    //             <input
    //               ref={fileInputRef}
    //               type="file"
    //               className="hidden"
    //               onChange={(e) => {
    //                 const selectedFile = e.target.files[0];
    //                 if (selectedFile) {
    //                   setFile(selectedFile);
    //                   sendFile(selectedFile);
    //                 }
    //                 e.target.value = ""; // Reset input
    //               }}
    //             />

    //             <div className="flex-1 bg-[#2A3942] rounded-lg">
    //               <input
    //                 type="text"
    //                 value={message}
    //                 onChange={(e) => setMessage(e.target.value)}
    //                 onKeyPress={handleKeyPress}
    //                 placeholder="Type a message"
    //                 className="w-full bg-transparent text-white px-4 py-3 outline-none text-sm"
    //               />
    //             </div>

    //             <button
    //               onClick={handleSendMessage}
    //               disabled={!message.trim()}
    //               className={`p-3 rounded-full transition-colors ${
    //                 message.trim()
    //                   ? "bg-[#25D366] hover:bg-[#20BD5F] text-[#111B21]"
    //                   : "bg-[#2A3942] text-[#8696A0]"
    //               }`}
    //             >
    //               <BiSolidSend className="text-xl" />
    //             </button>
    //           </div>
    //         </>
    //       ) : (
    //         <div className="flex-1 flex flex-col items-center justify-center text-[#8696A0]">
    //           <div className="text-center">
    //             <div className="w-20 h-20 rounded-full mx-auto mb-8 opacity-100">
    //               <img
    //                 src={
    //                   "https://icon-library.com/images/chat-icon-png/chat-icon-png-10.jpg"
    //                 }
    //               />
    //             </div>
    //             <h3 className="text-3xl font-light mb-2">Live Chat</h3>
    //             <p className="text-sm">Select a chat to start messaging</p>
    //           </div>
    //         </div>
    //       )}
    //     </div>
    //   </div>
    // </>

    <div className="flex h-[88vh] bg-[#111B21] overflow-hidden">
      {/* ================= Sidebar ================= */}
      <div
        className={`bg-[#111B21] text-white 
    w-full md:w-[30%] md:min-w-[340px]
    flex flex-col border-r border-[#2A3942]
    ${chat ? "hidden md:flex" : "flex"}`}
      >
        {/* Sidebar Header */}
        <div className="bg-[#202C33] px-4 py-3 flex justify-between items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-[#2A3942] rounded-full transition-colors"
          >
            <FaArrowLeft className="text-[#8696A0]" />
          </button>
          <h2 className="text-lg font-medium">Chats</h2>
          <button className="p-2 hover:bg-[#2A3942] rounded-full transition-colors">
            <PiDotsThreeOutlineVerticalFill className="text-[#8696A0]" />
          </button>
        </div>

        {/* Search */}
        <div className="px-3 py-2">
          <div className="bg-[#202C33] rounded-lg flex items-center px-4 py-2">
            <FaSearch className="text-[#8696A0] text-sm" />
            <input
              type="text"
              placeholder="Search or start new chat"
              className="bg-transparent text-white ml-4 flex-1 outline-none text-sm"
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-[#374045]">
          {customers?.data?.data?.map((customer, i) => (
            <div
              key={i}
              className={`flex items-center px-4 py-3 cursor-pointer hover:bg-[#202C33] ${
                customer?.sender_id === chat?.sender_id ? "bg-[#2A3942]" : ""
              }`}
              onClick={() => {
                setChat(customer);
                setMessages([]);
                setChatList([]);
                setPage(1);
                setHasMore(true);
                setIsInitialLoad(false);
                previousScrollHeight.current = 0;
              }}
            >
              <div className="w-12 h-12 rounded-full bg-[#6B7C85] flex items-center justify-center overflow-hidden flex-shrink-0">
                {customer?.profile_picture ? (
                  <img
                    src={customer.profile_picture}
                    alt={customer?.full_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-medium">
                    {customer?.full_name?.charAt(0) || "U"}
                  </span>
                )}
              </div>

              <div className="ml-4 border-b border-[#2A3942] pb-3 flex-1 min-w-0">
                <div className="flex justify-between items-baseline">
                  <h3 className="font-medium truncate">
                    {customer?.user_name}
                  </h3>
                  <span className="text-xs text-[#8696A0] ml-2">
                    {formatTime(customer?.last_message_time)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ================= Chat Area ================= */}
      <div
        className={`flex-1 flex flex-col bg-[#0B141A] relative
    ${!chat ? "hidden md:flex" : "flex"}`}
      >
        {chat ? (
          <>
            {/* Chat Header */}
            <div className="bg-[#202C33] px-4 py-3 flex items-center justify-between border-b border-[#2A3942]">
              <div className="flex items-center gap-3">
                {/* Mobile Back */}
                <button
                  onClick={() => setChat(null)}
                  className="md:hidden p-2 hover:bg-[#2A3942] rounded-full"
                >
                  <FaArrowLeft className="text-[#8696A0]" />
                </button>

                <div className="w-10 h-10 rounded-full bg-[#6B7C85]  overflow-hidden">
                  {chat?.profile_picture ? (
                    <img
                      src={chat.profile_picture}
                      alt={chat?.full_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="flex items-center justify-center h-full">
                      {chat?.full_name?.charAt(0) || "U"}
                    </span>
                  )}
                </div>

                <div>
                  <h3 className="font-medium  text-white">{chat?.user_name}</h3>
                  <p className="text-xs text-[#8696A0]">
                    {isTyping ? "typing..." : "online"}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={messageContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto px-3 sm:px-6 lg:px-16 py-4 scrollbar-thin scrollbar-thumb-[#374045]"
            >
              {chatList.map((msg, i) => (
                <div
                  key={i}
                  className={`flex mb-2  text-white ${
                    msg.sender_username === senders
                      ? "justify-end"
                      : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-[70%] lg:max-w-[65%] rounded-lg px-3 py-2 ${
                      msg.sender_username === senders
                        ? "bg-[#005C4B]"
                        : "bg-[#202C33]"
                    }`}
                  >
                    <p className="text-sm break-words">{msg.message}</p>
                    <div className="text-[11px] text-[#8696A0] text-right mt-1">
                      {formatTime(msg.timestamp)}
                    </div>
                  </div>
                </div>
              ))}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex mb-2 ${
                    msg.sender === senders ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-[70%] lg:max-w-[65%] rounded-lg px-3 py-2 ${
                      msg.sender === senders ? "bg-[#005C4B]" : "bg-[#202C33]"
                    }`}
                  >
                    <p className="text-sm break-words">{msg.message}</p>
                    <div className="text-[11px] text-[#8696A0] text-right mt-1">
                      {formatTime(msg.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-[#202C33] px-3 py-2 flex items-center gap-2">
              <button
                className="text-[#8696A0] p-2"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              >
                <BsEmojiSmile className="text-2xl" />
              </button>

              <div className="flex-1 bg-[#2A3942] rounded-lg min-w-0">
                <input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message"
                  className="w-full bg-transparent text-white px-4 py-3 outline-none text-sm"
                />
              </div>

              <button
                onClick={handleSendMessage}
                disabled={!message.trim()}
                className={`p-3 rounded-full ${
                  message.trim()
                    ? "bg-[#25D366] text-white"
                    : "bg-[#2A3942] text-[#8696A0]"
                }`}
              >
                <BiSolidSend className="text-xl" />
              </button>
            </div>
          </>
        ) : (
          <>
            {isLoading ? (
              <CircularProgress color="warning" />
            ) : (
              <div className="flex-1 flex items-center justify-center text-[#8696A0]">
                Select a chat to start messaging
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatAgent;
